import { supabase } from './customSupabaseClient.js';
import imageOptimizationAPI from '../api/imageOptimization.js';
import { resolveFleetCardImage } from '../constants/fleet.js';

/**
 * Optimized Fleet Service
 * Extends the fleet service with automatic image optimization
 */
class OptimizedFleetService {
  constructor() {
    this.originalFleetService = null;
  }

  // Set the original fleet service
  setOriginalService(service) {
    this.originalFleetService = service;
  }

  /**
   * Add a new vehicle with automatic image optimization
   */
  async addVehicle(vehicleData) {
    try {
      // Note: Image optimization is now handled by the file upload service
      // This service focuses on database operations

      // Add the vehicle using the original service
      if (this.originalFleetService) {
        return await this.originalFleetService.addVehicle(vehicleData);
      } else {
        // Fallback to direct Supabase call
        const { data, error } = await supabase
          .from('fleet')
          .insert([vehicleData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error adding vehicle:', error);
      throw error;
    }
  }

  /**
   * Update a vehicle with automatic image optimization
   */
  async updateVehicle(id, vehicleData) {
    try {
      // Note: Image optimization is now handled by the file upload service
      // This service focuses on database operations

      // Update the vehicle using the original service
      if (this.originalFleetService) {
        return await this.originalFleetService.updateVehicle(id, vehicleData);
      } else {
        // Fallback to direct Supabase call
        const { data, error } = await supabase
          .from('fleet')
          .update(vehicleData)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error updating vehicle:', error);
      throw error;
    }
  }

  /**
   * Get all vehicles with optimized image paths
   */
  async getVehicles() {
    try {
      if (this.originalFleetService) {
        const vehicles = await this.originalFleetService.getFleet();
        return this.enhanceVehiclesWithOptimizedImages(vehicles);
      } else {
        // Fallback to direct Supabase call
        const { data, error } = await supabase
          .from('fleet')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        return this.enhanceVehiclesWithOptimizedImages(data || []);
      }
    } catch (error) {
      console.error('Error getting vehicles:', error);
      throw error;
    }
  }

  /**
   * Rewrite legacy /uploads/... paths to deployed /images/fleet/... assets
   */
  normalizeFleetImageUrl(url) {
    if (!url || typeof url !== 'string') return null;

    let path = url.trim();

    // Absolute same-origin URLs → path only
    try {
      if (/^https?:\/\//i.test(path)) {
        const parsed = new URL(path);
        path = parsed.pathname + parsed.search;
      }
    } catch {
      // keep as-is
    }

    // /uploads/{type}/optimized/{file} → /images/fleet/{type}/optimized/{file}
    const uploadsMatch = path.match(
      /^\/uploads\/([^/]+)\/optimized\/([^/?#]+)(\?.*)?$/i
    );
    if (uploadsMatch) {
      const typeDir = uploadsMatch[1].toLowerCase();
      let fileName = uploadsMatch[2];
      const query = uploadsMatch[3] || '';

      // Strip hashed suffixes: executive-sedan-1761269934281.webp → executive-sedan.webp
      fileName = fileName.replace(/-\d{10,}(\.(webp|jpe?g|png|gif))$/i, '$1');

      return `/images/fleet/${typeDir}/optimized/${fileName}${query}`;
    }

    // Broader /uploads/... → /images/fleet/...
    if (path.startsWith('/uploads/')) {
      let rewritten = path.replace(/^\/uploads\//, '/images/fleet/');
      rewritten = rewritten.replace(/-\d{10,}(\.(webp|jpe?g|png|gif))(\?.*)?$/i, '$1$3');
      return rewritten;
    }

    return path;
  }

  /**
   * Enhance vehicles with optimized image paths
   */
  enhanceVehiclesWithOptimizedImages(vehicles) {
    // Prefer space-free *-card.jpg assets (reliable on Hostinger)
    // resolveFleetCardImage matches by name or type (case-insensitive)
    return vehicles.map(vehicle => {
      const cardImage = resolveFleetCardImage(vehicle);
      if (cardImage) {
        return {
          ...vehicle,
          image_url: cardImage,
          image_url_fallback: cardImage
        };
      }

      const normalized = this.normalizeFleetImageUrl(vehicle.image_url);
      const fallback = vehicle.image_url_fallback || null;
      const newImageUrl = normalized || fallback;

      return {
        ...vehicle,
        image_url: newImageUrl,
        image_url_fallback: fallback
      };
    });
  }

  /**
   * Optimize existing vehicle images
   * Note: This is now handled by the file upload service
   */
  async optimizeExistingImages() {
    // Image optimization is now handled by the file upload service
    return [];
  }

  // Delegate other methods to the original service
  async deleteVehicle(id) {
    if (this.originalFleetService) {
      return await this.originalFleetService.deleteVehicle(id);
    } else {
      const { error } = await supabase
        .from('fleet')
        .delete()
        .eq('id', id);
      if (error) throw error;
    }
  }

  async getVehicle(id) {
    if (this.originalFleetService) {
      // Try getVehicleById first, then getVehicle as fallback
      if (this.originalFleetService.getVehicleById) {
        return await this.originalFleetService.getVehicleById(id);
      } else if (this.originalFleetService.getVehicle) {
        return await this.originalFleetService.getVehicle(id);
      }
    }
    
    // Fallback to direct Supabase call
    const { data, error } = await supabase
      .from('fleet')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  }
}

// Create singleton instance
const optimizedFleetService = new OptimizedFleetService();

export default optimizedFleetService;
